<template>
  <div
    v-if="hasPartnerWorkflows"
    ref="partnerSelectionRoot"
    class="partner-selection"
  >
    <div class="primary-grey--text text-body-2 font-weight-bold mb-2">
      Trusted Partner Spotlight
    </div>
    <div class="primary-grey--text text-body-2 mb-4">
      Based on your question, we thought you might be interested to learn more about this partner
    </div>

    <GCarousel class="ma-2">
      <GCarouselItem
        v-for="partner in partnerWorkflows"
        :key="partner.id"
      >
        <v-card
          class="partner-card d-flex flex-column"
          outlined
        >
          <v-card-text class="pa-4 d-flex flex-column flex-grow-1">
            <!-- Header: Logo + Name + Descriptor -->
            <div class="d-flex align-start mb-3">
              <v-avatar
                size="48"
                class="mr-3"
              >
                <v-img
                  v-if="partner.logo_url"
                  :src="partner.logo_url"
                  :alt="partner.name"
                  eager
                >
                  <template #placeholder>
                    <div class="d-flex align-center justify-center fill-height">
                      <v-progress-circular
                        indeterminate
                        size="20"
                        width="2"
                        color="grey lighten-1"
                      />
                    </div>
                  </template>
                </v-img>
                <v-icon
                  v-else
                  size="32"
                  color="grey"
                >
                  mdi-domain
                </v-icon>
              </v-avatar>
              <div class="partner-header-text">
                <div class="text-subtitle-1 font-weight-bold primary-grey--text">
                  {{ partner.name }}
                </div>
                <div
                  v-if="getDescriptor(partner)"
                  class="text-caption grey--text"
                >
                  {{ getDescriptor(partner) }}
                </div>
              </div>
            </div>

            <!-- Headline: Bold attention-grabbing message -->
            <div
              v-if="getHeadline(partner)"
              class="text-body-2 font-weight-medium primary-grey--text mb-2 partner-headline"
            >
              {{ getHeadline(partner) }}
            </div>

            <!-- Body: Full description (replaces old description field) -->
            <div
              v-if="getBody(partner)"
              class="text-body-2 primary-grey--text partner-body"
            >
              {{ getBody(partner) }}
            </div>

            <!-- CTAs with action type-specific icons -->
            <div
              v-if="partner.ctas && partner.ctas.length > 0"
              class="d-flex pt-4 mt-auto"
              style="gap: 8px; width: 100%;"
            >
              <v-btn
                v-for="(cta, ctaIndex) in partner.ctas"
                :key="cta.id"
                :outlined="isOutlinedButton(ctaIndex, partner.ctas.length)"
                :color="COLORS.grainfoxAIButton"
                :class="{ 'white--text': !isOutlinedButton(ctaIndex, partner.ctas.length) }"
                small
                class="partner-cta-btn"
                style="flex: 1 1 0; min-width: 0;"
                @click="handleCtaClick(cta)"
              >
                <v-icon
                  v-if="getCtaIcon(cta.action_type)"
                  small
                  left
                >
                  {{ getCtaIcon(cta.action_type) }}
                </v-icon>
                {{ cta.name }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </GCarouselItem>
    </GCarousel>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { COLORS } from '../../helpers/colors';
import GCarousel from '../generic/GCarousel.vue';
import GCarouselItem from '../generic/GCarouselItem.vue';

// CTA action type to icon mapping
const ACTION_TYPE_ICONS = {
  link: 'mdi-open-in-new',
  phone: 'mdi-phone',
  email: 'mdi-email',
};

export default {
  name: 'PartnerSelection',

  components: {
    GCarousel,
    GCarouselItem,
  },

  data() {
    return {
      COLORS,
    };
  },

  computed: {
    ...mapState('chatbot', ['partnerWorkflows']),
    ...mapGetters('chatbot', ['hasPartnerWorkflows', 'isStreaming']),
  },

  watch: {
    /**
     * When partner workflows appear AND streaming has ended, scroll to this component.
     * We check both conditions to ensure we only scroll after the streaming completes.
     */
    hasPartnerWorkflows(newVal) {
      if (newVal && !this.isStreaming) {
        this.emitScrollToPartners();
      }
    },

    isStreaming(newVal) {
      // When streaming ends (false) and we have partner workflows, scroll to them
      if (!newVal && this.hasPartnerWorkflows) {
        this.emitScrollToPartners();
      }
    },
  },

  methods: {
    ...mapActions('chatbot', ['trackCtaClick']),

    /**
     * Extracts descriptor from partner object.
     * Supports both nested (description.descriptor) and flat (descriptor) structures.
     * @param {Object} partner - The partner workflow object
     * @returns {string|null} - Descriptor text or null
     */
    getDescriptor(partner) {
      return partner?.description?.descriptor || partner?.descriptor || null;
    },

    /**
     * Extracts headline from partner object.
     * Supports both nested (description.headline) and flat (headline) structures.
     * @param {Object} partner - The partner workflow object
     * @returns {string|null} - Headline text or null
     */
    getHeadline(partner) {
      return partner?.description?.headline || partner?.headline || null;
    },

    /**
     * Extracts body from partner object.
     * Supports both nested (description.body) and flat (body) structures.
     * @param {Object} partner - The partner workflow object
     * @returns {string|null} - Body text or null
     */
    getBody(partner) {
      return partner?.description?.body || partner?.body || null;
    },

    /**
     * Returns the appropriate icon for a CTA action type.
     * @param {string} actionType - The action type (link, phone, email)
     * @returns {string|null} - MDI icon name or null if no icon needed
     */
    getCtaIcon(actionType) {
      return ACTION_TYPE_ICONS[actionType] || null;
    },

    /**
     * Opens CTA based on action_type and tracks the click for analytics.
     * - link: Opens action_value URL in new tab
     * - phone: Opens tel: link (triggers phone dialer)
     * - email: Opens mailto: link (triggers email client)
     * @param {Object} cta - The CTA object with action_type, action_value, and id
     */
    handleCtaClick(cta) {
      // Fire-and-forget analytics tracking (does not block navigation)
      if (cta?.id) {
        this.trackCtaClick(cta.id);
      }

      const actionValue = cta?.action_value;
      if (!actionValue) return;

      const actionType = cta?.action_type || 'link';

      switch (actionType) {
        case 'phone':
          // Clean phone number and create tel: link
          window.open(`tel:${actionValue.replace(/\s/g, '')}`, '_self');
          break;
        case 'email':
          // Create mailto: link
          window.open(`mailto:${actionValue}`, '_self');
          break;
        case 'link':
        default:
          // Open URL in new tab (default behavior)
          window.open(actionValue, '_blank', 'noopener,noreferrer');
          break;
      }
    },

    /**
     * Determines if button should be outlined (transparent background) based on index and count.
     * First button is outlined if 2+ CTAs present; otherwise all buttons are filled (primary).
     */
    isOutlinedButton(index, totalCtas) {
      return totalCtas > 1 && index === 0;
    },

    /**
     * Emits scroll-to-partners event with the DOM element reference.
     * Parent component (ChatMessages → GrainFoxAssistantDialog) will scroll to this element.
     */
    emitScrollToPartners() {
      this.$nextTick(() => {
        if (this.$refs.partnerSelectionRoot) {
          this.$emit('scroll-to-partners', this.$refs.partnerSelectionRoot);
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
.partner-selection {
  margin-top: 16px;

  /* Override GCarousel items container to keep cards together (not spread apart) */
  ::v-deep .g-carousel__items {
    justify-content: flex-start;
  }
}

.partner-card {
  border: 1px solid #12A8C4 !important;
  background-color: #E9F6FD !important;
  border-radius: 8px !important;
  width: 320px;
  min-width: 280px;
  max-width: 360px;
}

.partner-header-text {
  flex: 1;
  min-width: 0;
}

.partner-headline {
  /* Limit headline to 2 lines with ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.partner-body {
  /* Limit body to 4 lines with ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.partner-cta-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  padding: 0 12px !important;
}

/* Proper icon spacing inside buttons */
.partner-cta-btn .v-icon {
  margin-right: 6px;
}

/* Hover state for outlined buttons only */
.partner-cta-btn.v-btn--outlined:hover {
  background-color: rgba(18, 168, 196, 0.08) !important;
}

/* Hover state for filled buttons - slightly darker */
.partner-cta-btn:not(.v-btn--outlined):hover {
  background-color: #109bb5 !important;
}

/* Mobile responsiveness - similar to PlanSelection */
@media (max-width: 600px) {
  /* Constrain the carousel container to prevent overflow */
  ::v-deep .g-carousel {
    max-width: 100vw;
    overflow: hidden;
  }

  /* Override partner card width for mobile */
  .partner-card {
    width: 260px !important;
    min-width: 260px !important;
    max-width: 260px !important;
  }

  /* Ensure carousel items container is properly constrained */
  ::v-deep .g-carousel__items {
    max-width: calc(100vw - 48px);
  }
}
</style>
